import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import TabMenu from '../../navigation/TabMenu'
import SettingTabsInfo from '../../../constants/SettingTabsInfo'
import { SETTINGS_BIZ_ACCESS_LOGS } from '../../../constants/Paths'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { itemIcon } from '../../../icons'
import {
  BizAccessLogEventType,
  BizAccessLogSearchParams,
  fetchBizAccessLogs,
} from '../../../modules/BizAccessLog'
import { getTeamGroups } from '../../../modules/Team'
import { getErrorString } from '../../../utils/errorUtils'
import UserPagination from '../../common/UserPagination'
import InlineLoadingAnimation from '../../common/InlineLoadingAnimation'
import './SettingsBizAccessLogs.css'

type FilterState = {
  eventType: BizAccessLogEventType | ''
  userId: string
  path: string
  startAt: string
  endAt: string
}

type HttpError = Error & { status?: number }

const EVENT_TYPE_LABELS: Record<string, string> = {
  login: 'ログイン',
  api_access: 'APIアクセス',
  qr_tag_view: 'QRタグ閲覧',
}

const formatEventType = (eventType?: string | null): string => (
  eventType ? (EVENT_TYPE_LABELS[eventType] ?? eventType) : '-'
)

const formatLoggedAt = (value?: string | null): string => {
  if (!value) return '-'
  return moment(value).utcOffset(540).format('YYYY/MM/DD HH:mm')
}

const normalizeDateTimeParam = (value: string): string | undefined => {
  if (!value) return undefined
  return moment(value).format()
}

const buildUserLabel = (log: IBizAccessLog): { name: string; email?: string | null } => {
  if (log.user) {
    return {
      name: log.user.name || log.user.email || `ID: ${log.user.id}`,
      email: log.user.email,
    }
  }
  if (log.user_id) {
    return { name: `ID: ${log.user_id}` }
  }
  return { name: '-' }
}

type MamorioSummary = Pick<IMamorio, 'id' | 'name' | 'image' | 'user'>

const buildMamorioLabel = (mamorio?: MamorioSummary | null): {
  name: string
  ownerName: string
  imageUrl: string
} => {
  if (!mamorio) {
    return { name: '-', ownerName: '-', imageUrl: itemIcon }
  }
  const ownerName = mamorio.user?.name
    || mamorio.user?.email
    || (mamorio.user?.id ? `ID: ${mamorio.user.id}` : '-')
  const imageUrl = mamorio.image?.normal?.url
    ?? mamorio.image?.url
    ?? mamorio.image?.retina?.url
    ?? itemIcon
  return {
    name: mamorio.name || `ID: ${mamorio.id}`,
    ownerName,
    imageUrl,
  }
}

const parseMamorioIdFromPath = (path?: string | null): number | null => {
  if (!path) return null
  const match = path.match(/\/(?:group_mamorios|team_mamorios)\/(\d+)/)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isNaN(parsed) ? null : parsed
}

const buildCsvValue = (value: string): string => {
  const escaped = value.replace(/"/g, '""')
  return `"${escaped}"`
}

const buildCsvRow = (columns: string[]): string => (
  columns.map((column) => buildCsvValue(column)).join(',')
)

const isForbiddenError = (error: unknown): boolean => (
  error instanceof Error && (error as HttpError).status === 403
)

const SettingsBizAccessLogs = () => {
  const dispatch = useAppDispatch()
  const team = useAppSelector((state) => state.team)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios) ?? []
  const history = useHistory()
  const tabsInfo = useMemo(
    () => SettingTabsInfo({
      groups: team.groups ?? null,
      deviceEnabled: team.device_enabled,
      qrTagEnabled: team.qr_tag_enabled ?? false,
      rfidTagEnabled: team.rfid_tag_enabled ?? false,
      eventNotificationSettingsEnable: team.event_notification_settings_enable ?? false,
    }),
    [
      team.device_enabled,
      team.event_notification_settings_enable,
      team.groups,
      team.qr_tag_enabled,
      team.rfid_tag_enabled,
    ],
  )
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    eventType: '',
    userId: '',
    path: '',
    startAt: '',
    endAt: '',
  })
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    eventType: '',
    userId: '',
    path: '',
    startAt: '',
    endAt: '',
  })
  const [perPage, setPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [bizAccessLogs, setBizAccessLogs] = useState<IBizAccessLog[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)

  useEffect(() => {
    dispatch(getTeamGroups(false))
  }, [dispatch])

  const logEnabledGroups = useMemo(
    () => (team.groups ?? []).filter((group) => group.biz_access_log_enabled),
    [team.groups],
  )
  const selectedGroup = useMemo(
    () => logEnabledGroups.find((group) => group.id === selectedGroupId) ?? null,
    [logEnabledGroups, selectedGroupId],
  )
  const availableUsers = useMemo(
    () => selectedGroup?.users ?? [],
    [selectedGroup],
  )

  useEffect(() => {
    if (logEnabledGroups.length === 0) {
      setSelectedGroupId(null)
      return
    }

    const hasSelected = logEnabledGroups.some((group) => group.id === selectedGroupId)
    const firstGroup = logEnabledGroups[0]
    if (!hasSelected && firstGroup) {
      setSelectedGroupId(firstGroup.id)
      setCurrentPage(1)
    }
  }, [logEnabledGroups, selectedGroupId])

  const fetchLogs = useCallback(async () => {
    if (!selectedGroupId) return

    setLoading(true)
    setError(null)
    setForbidden(false)

    const parsedUserId = Number(appliedFilters.userId)
    const userId = appliedFilters.userId && !Number.isNaN(parsedUserId) ? parsedUserId : undefined
    const path = appliedFilters.path.trim()
    const params: BizAccessLogSearchParams = {
      event_type: appliedFilters.eventType || undefined,
      user_id: userId,
      path: path || undefined,
      start_at: normalizeDateTimeParam(appliedFilters.startAt),
      end_at: normalizeDateTimeParam(appliedFilters.endAt),
      page: currentPage,
      per_page: perPage,
    }

    try {
      const result = await fetchBizAccessLogs(selectedGroupId, params)
      const logs = result.bizAccessLogs
      const derivedTotalPages = result.totalPages
        ?? (logs.length < perPage ? currentPage : currentPage + 1)

      setBizAccessLogs(logs)
      setTotalPages(Math.max(derivedTotalPages, 1))
      if (result.currentPage && result.currentPage !== currentPage) {
        setCurrentPage(result.currentPage)
      }
    } catch (err) {
      if (isForbiddenError(err)) {
        setForbidden(true)
      } else {
        setError(getErrorString(err))
      }
      setBizAccessLogs([])
    } finally {
      setLoading(false)
    }
  }, [appliedFilters, currentPage, perPage, selectedGroupId])

  useEffect(() => {
    if (!selectedGroupId) return
    fetchLogs()
  }, [fetchLogs, selectedGroupId])

  const handleGroupChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroupId(Number(event.target.value) as GroupId)
    setFilters((prev) => ({ ...prev, userId: '' }))
    setAppliedFilters((prev) => ({ ...prev, userId: '' }))
    setCurrentPage(1)
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault()
    setCurrentPage(1)
    setAppliedFilters({ ...filters })
  }

  const handleReset = () => {
    const cleared: FilterState = {
      eventType: '',
      userId: '',
      path: '',
      startAt: '',
      endAt: '',
    }
    setFilters(cleared)
    setAppliedFilters(cleared)
    setCurrentPage(1)
  }

  const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(event.target.value))
    setCurrentPage(1)
  }

  const resolveMamorioFromLog = (log: IBizAccessLog): MamorioSummary | null => {
    const mamorioId = log.target_id ?? parseMamorioIdFromPath(log.path)
    const mamorioFromStore = mamorioId
      ? mamorios.find((mamorio) => mamorio.id === mamorioId) ?? null
      : null

    return log.target_mamorio ?? mamorioFromStore
  }

  const handleDownloadCsv = () => {
    if (bizAccessLogs.length === 0) return

    const header = buildCsvRow([
      'logged_at',
      'access_user_name',
      'access_user_email',
      'mamorio_name',
      'mamorio_owner_name',
      'mamorio_owner_email',
      'event_type',
      'path',
      'ip_address',
      'comment',
    ])
    const rows = bizAccessLogs.map((log) => {
      const userLabel = buildUserLabel(log)
      const resolvedMamorio = resolveMamorioFromLog(log)
      const mamorioLabel = buildMamorioLabel(resolvedMamorio)
      const mamorioOwnerEmail = resolvedMamorio?.user?.email ?? '-'
      return buildCsvRow([
        formatLoggedAt(log.logged_at),
        userLabel.name,
        userLabel.email ?? '-',
        mamorioLabel.name,
        mamorioLabel.ownerName,
        mamorioOwnerEmail,
        formatEventType(log.event_type),
        `${log.request_method ?? ''} ${log.path ?? ''}`.trim() || '-',
        log.ip_address ?? '-',
        log.comment?.trim() ? log.comment : '-',
      ])
    })
    const csvContent = [header, ...rows].join('\n')
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = moment().format('YYYYMMDD_HHmmss')
    link.href = url
    link.setAttribute('download', `biz_access_logs_${timestamp}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleOpenDetail = (log: IBizAccessLog) => {
    if (!selectedGroupId) return
    history.push({
      pathname: `${SETTINGS_BIZ_ACCESS_LOGS}/${log.id}`,
      state: {
        log,
        groupId: selectedGroupId,
      },
    })
  }

  const hasLogAccess = logEnabledGroups.length > 0
  const renderTableContent = () => {
    if (loading) {
      return (
        <div className="biz-access-logs__loading">
          <InlineLoadingAnimation size="32px" color="#666" />
          <span>読み込み中...</span>
        </div>
      )
    }

    if (bizAccessLogs.length === 0) {
      return (
        <p className="biz-access-logs__empty">
          該当するログがありません。
        </p>
      )
    }

    return (
      <table className="biz-access-logs__table">
        <thead>
          <tr>
            <th>logged_at</th>
            <th>user</th>
            <th>mamorio</th>
            <th>event_type</th>
            <th>path</th>
            <th>ip_address</th>
            <th>comment</th>
            <th>detail</th>
          </tr>
        </thead>
        <tbody>
          {bizAccessLogs.map((log) => {
            const userLabel = buildUserLabel(log)
            const resolvedMamorio = resolveMamorioFromLog(log)
            const mamorioLabel = buildMamorioLabel(resolvedMamorio)
            const hasMamorio = Boolean(resolvedMamorio)

            return (
              <tr key={log.id}>
                <td>{formatLoggedAt(log.logged_at)}</td>
                <td>
                  <div className="biz-access-logs__user-name">{userLabel.name}</div>
                  {userLabel.email && (
                    <div className="biz-access-logs__user-email">{userLabel.email}</div>
                  )}
                </td>
                <td>
                  {hasMamorio ? (
                    <div className="biz-access-logs__mamorio">
                      <img
                        src={mamorioLabel.imageUrl}
                        alt={mamorioLabel.name}
                        className="biz-access-logs__mamorio-image"
                        onError={(event) => {
                          const target = event.currentTarget
                          target.src = itemIcon
                        }}
                      />
                      <div>
                        <div className="biz-access-logs__mamorio-name">{mamorioLabel.name}</div>
                        <div className="biz-access-logs__mamorio-owner">
                          所持者:
                          {' '}
                          {mamorioLabel.ownerName}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="biz-access-logs__placeholder">-</span>
                  )}
                </td>
                <td>{formatEventType(log.event_type)}</td>
                <td>
                  {log.request_method && (
                    <span className="biz-access-logs__method">
                      {log.request_method}
                    </span>
                  )}
                  <span className="biz-access-logs__path">{log.path ?? '-'}</span>
                </td>
                <td>{log.ip_address ?? '-'}</td>
                <td>
                  <div className="biz-access-logs__comment-text">
                    {log.comment?.trim() ? log.comment : '-'}
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleOpenDetail(log)}
                  >
                    詳細
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  return (
    <TabMenu tabsInfo={tabsInfo}>
      <div className="biz-access-logs">
        <div className="biz-access-logs__header">
          <div>
            <h2 className="biz-access-logs__title">Bizアクセスログ</h2>
            <p className="biz-access-logs__description">
              Biz利用時の操作履歴を確認し、必要に応じてコメントを残せます。
            </p>
          </div>
        </div>

        {!hasLogAccess && (
          <p className="biz-access-logs__empty">
            Bizアクセスログが有効なグループがありません。
          </p>
        )}

        {hasLogAccess && (
          <>
            <form className="biz-access-logs__filters" onSubmit={handleSearch}>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-group">グループ</label>
                <select
                  id="biz-access-log-group"
                  value={selectedGroupId ?? ''}
                  onChange={handleGroupChange}
                >
                  {logEnabledGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-user">ユーザー</label>
                <select
                  id="biz-access-log-user"
                  value={filters.userId}
                  onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
                >
                  <option value="">すべて</option>
                  {availableUsers.map((user) => {
                    const label = user.name
                      ? `${user.name} (${user.email})`
                      : user.email
                    return (
                      <option key={user.id} value={user.id}>
                        {label}
                      </option>
                    )
                  })}
                </select>
              </div>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-path">パス</label>
                <input
                  id="biz-access-log-path"
                  type="text"
                  value={filters.path}
                  onChange={(event) => setFilters((prev) => ({ ...prev, path: event.target.value }))}
                  placeholder="/api/v1/..."
                />
              </div>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-start">開始日時</label>
                <input
                  id="biz-access-log-start"
                  type="datetime-local"
                  value={filters.startAt}
                  onChange={(event) => setFilters((prev) => ({ ...prev, startAt: event.target.value }))}
                />
              </div>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-end">終了日時</label>
                <input
                  id="biz-access-log-end"
                  type="datetime-local"
                  value={filters.endAt}
                  onChange={(event) => setFilters((prev) => ({ ...prev, endAt: event.target.value }))}
                />
              </div>
              <div className="biz-access-logs__filter">
                <label htmlFor="biz-access-log-per-page">表示件数</label>
                <select
                  id="biz-access-log-per-page"
                  value={perPage}
                  onChange={handlePerPageChange}
                >
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              <div className="biz-access-logs__filter-actions">
                <button type="submit" className="btn btn-primary">
                  検索
                </button>
                <button type="button" className="btn btn-outline-primary" onClick={handleReset}>
                  リセット
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleDownloadCsv}
                  disabled={bizAccessLogs.length === 0 || loading}
                >
                  CSVダウンロード
                </button>
              </div>
            </form>

            {forbidden && (
              <p className="biz-access-logs__error">
                このグループではBizアクセスログを利用できません。
              </p>
            )}

            {!forbidden && error && (
              <p className="biz-access-logs__error">{error}</p>
            )}

            {!forbidden && (
              <div className="biz-access-logs__table-wrapper">
                {renderTableContent()}
              </div>
            )}

            {!forbidden && totalPages > 1 && (
              <UserPagination
                currentPageNumber={currentPage}
                totalPages={totalPages}
                onChangePage={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </TabMenu>
  )
}

export default SettingsBizAccessLogs
